import { colorFor, initials } from '@hudhuria/shared';
import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { apiUrl } from '~/lib/api';
import { qrMatrix } from '~/lib/qr';
import { useToast } from '~/lib/toast';
import { ChevronLeft, SearchIcon } from '../icons';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

// ---------------------------------------------------------------- brand

/** The interim attendance-ring mark ("echoing the Hub dial"). */
export function RingLogo({ size = 32, light = false }: { size?: number; light?: boolean }) {
  const inner = Math.round(size * 0.58);
  const dot = Math.round(size * 0.22);
  const ring = light
    ? 'conic-gradient(#fff 0 75%, rgba(255,255,255,0.35) 75% 100%)'
    : 'conic-gradient(#0369a1 0 75%, #bae6fd 75% 100%)';
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full"
      style={{ width: size, height: size, background: ring }}
    >
      <div
        className="flex items-center justify-center rounded-full"
        style={{ width: inner, height: inner, background: light ? '#0369a1' : '#fff' }}
      >
        {!light && <div className="rounded-full bg-brand" style={{ width: dot, height: dot }} />}
      </div>
    </div>
  );
}

export function Wordmark({ size = 17 }: { size?: number }) {
  return (
    <span className="font-display font-bold" style={{ fontSize: size, letterSpacing: '-0.02em' }}>
      hudhuria
    </span>
  );
}

// ---------------------------------------------------------------- buttons

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean };

export function PrimaryButton({ className, children, loading, disabled, ...p }: BtnProps) {
  return (
    <button
      type="button"
      {...p}
      disabled={disabled || loading}
      className={cx(
        'flex w-full cursor-pointer items-center justify-center gap-[9px] rounded-[14px] border-none bg-brand p-4 text-[15.5px] font-semibold text-white transition-opacity active:opacity-90 disabled:cursor-default disabled:opacity-60',
        className,
      )}
    >
      {loading ? <Spinner light /> : children}
    </button>
  );
}

export function SecondaryButton({ className, children, ...p }: BtnProps) {
  return (
    <button
      type="button"
      {...p}
      className={cx(
        'flex w-full cursor-pointer items-center justify-center gap-[9px] rounded-[14px] border border-slate-200 bg-white p-[15px] text-[15px] font-semibold text-ink active:bg-fill-soft disabled:opacity-60',
        className,
      )}
    >
      {children}
    </button>
  );
}

export function Spinner({ light = false, size = 18 }: { light?: boolean; size?: number }) {
  return (
    <span
      className="inline-block animate-spin rounded-full border-2"
      style={{
        width: size,
        height: size,
        borderColor: light ? 'rgba(255,255,255,0.35)' : '#e2e8f0',
        borderTopColor: light ? '#fff' : '#0369a1',
      }}
    />
  );
}

// ---------------------------------------------------------------- fields

export const FieldLabel = ({ children }: { children: ReactNode }) => (
  <div className="mb-[7px] text-[12.5px] font-semibold text-slate-600">{children}</div>
);

export const FieldError = ({ children }: { children?: ReactNode }) =>
  children ? <div className="mt-1.5 text-[12px] font-medium text-red-500">{children}</div> : null;

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  error?: string;
  compact?: boolean;
};

export const TextField = forwardRef<HTMLInputElement, InputProps>(function TextField(
  { label, error, compact, className, id, ...p },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className={className}>
      {label && (
        <label htmlFor={inputId} className="mb-[7px] block text-[12.5px] font-semibold text-slate-600">
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        {...p}
        className={cx(
          'w-full rounded-[13px] border bg-white text-[15px] outline-none transition-colors focus:border-brand',
          compact ? 'px-[14px] py-[13px]' : 'px-[15px] py-[14px]',
          error ? 'border-red-400' : 'border-slate-200',
        )}
      />
      <FieldError>{error}</FieldError>
    </div>
  );
});

export function SearchField({
  value,
  onChange,
  placeholder,
  trailing,
  className,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  trailing?: ReactNode;
  className?: string;
  autoFocus?: boolean;
}) {
  return (
    <div className={cx('flex items-center gap-[9px] rounded-[13px] bg-fill px-[14px] py-[11px]', className)}>
      <SearchIcon />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        // biome-ignore lint/a11y/noAutofocus: opt-in, for search-first sheets
        autoFocus={autoFocus}
        className="min-w-0 flex-1 border-none bg-transparent text-[15px] outline-none"
      />
      {trailing}
    </div>
  );
}

// ---------------------------------------------------------------- selection

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  tone = 'faint',
  size = 'md',
  className,
}: {
  options: { value: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  tone?: 'faint' | 'seg' | 'slate';
  size?: 'sm' | 'md';
  className?: string;
}) {
  const bg = tone === 'seg' ? 'bg-seg' : tone === 'slate' ? 'bg-slate-200' : 'bg-line-faint';
  return (
    <div className={cx('flex gap-1 rounded-[12px] p-1', bg, className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            type="button"
            key={o.value}
            onClick={() => onChange(o.value)}
            className={cx(
              'flex-1 cursor-pointer rounded-[9px] border-none text-center font-semibold transition-colors',
              size === 'sm' ? 'px-1 py-2 text-[12.5px]' : 'px-1 py-[9px] text-[13px]',
              on
                ? 'bg-white text-ink shadow-[0_1px_3px_rgba(15,23,42,0.12)]'
                : 'bg-transparent text-slate-500',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Chip({
  on,
  onClick,
  children,
  className,
}: {
  on: boolean;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'shrink-0 cursor-pointer whitespace-nowrap rounded-[20px] border-none px-[13px] py-[7px] text-[12.5px] font-semibold',
        on ? 'bg-brand text-white' : 'bg-fill text-slate-700',
        className,
      )}
    >
      {children}
    </button>
  );
}

export function Toggle({
  on,
  onChange,
  label,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      className="relative h-[26px] w-[44px] shrink-0 cursor-pointer rounded-[13px] border-none transition-colors"
      style={{ background: on ? '#0369a1' : '#cbd5e1' }}
    >
      <span
        className="absolute top-[3px] h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-[left]"
        style={{ left: on ? 21 : 3 }}
      />
    </button>
  );
}

export function Checkbox({ on }: { on: boolean }) {
  return (
    <div
      className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[7px] text-[12px] font-bold text-white"
      style={{ background: on ? '#0369a1' : '#fff', border: `2px solid ${on ? '#0369a1' : '#cbd5e1'}` }}
    >
      {on ? '✓' : ''}
    </div>
  );
}

export function Radio({ on }: { on: boolean }) {
  return (
    <div
      className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full"
      style={{ border: `2px solid ${on ? '#0369a1' : '#cbd5e1'}`, background: on ? '#0369a1' : '#fff' }}
    >
      <div className="h-2 w-2 rounded-full bg-white" />
    </div>
  );
}

// ---------------------------------------------------------------- people

export function Avatar({
  name,
  size = 42,
  radius = 13,
  fontSize,
  color,
  photoUrl,
  className,
  style,
  children,
  display = false,
}: {
  name: string;
  size?: number;
  /** px radius, or 'full' */
  radius?: number | 'full';
  fontSize?: number;
  color?: string;
  photoUrl?: string | null;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  display?: boolean;
}) {
  const src = apiUrl(photoUrl);
  return (
    <div
      className={cx(
        'relative flex shrink-0 items-center justify-center font-bold text-white',
        display && 'font-display',
        className,
      )}
      style={{
        width: size,
        height: size,
        borderRadius: radius === 'full' ? '50%' : radius,
        background: color ?? colorFor(name),
        fontSize: fontSize ?? Math.round(size * 0.32),
        ...style,
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          style={{ borderRadius: radius === 'full' ? '50%' : radius }}
        />
      ) : (
        initials(name) || '?'
      )}
      {children}
    </div>
  );
}

/** Small status dot pinned to an avatar corner. */
export const AvatarDot = ({
  color,
  size = 12,
  border = 2,
  offset = -2,
}: {
  color: string;
  size?: number;
  border?: number;
  offset?: number;
}) => (
  <div
    className="absolute rounded-full"
    style={{
      right: offset,
      bottom: offset,
      width: size,
      height: size,
      background: color,
      border: `${border}px solid #fff`,
    }}
  />
);

// ---------------------------------------------------------------- layout

export const SectionLabel = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cx('text-[12px] font-bold uppercase tracking-[0.06em] text-slate-400', className)}>
    {children}
  </div>
);

export function BackLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex cursor-pointer items-center gap-[5px] border-none bg-transparent p-0 text-[14px] font-semibold text-brand"
    >
      <ChevronLeft />
      {label}
    </button>
  );
}

/** Full-screen screen that slides in over the tabs (design "overlays", z-30). */
export function Overlay({
  children,
  className,
  tone = 'white',
  z = 30,
  animation = 'slide',
}: {
  children: ReactNode;
  className?: string;
  tone?: 'white' | 'soft';
  z?: number;
  animation?: 'slide' | 'sheet';
}) {
  return (
    <div
      className={cx(
        'absolute inset-0 flex flex-col',
        animation === 'slide' ? 'animate-slide-in' : 'animate-sheet-up',
        tone === 'soft' ? 'bg-fill-soft' : 'bg-white',
        className,
      )}
      style={{ zIndex: z }}
    >
      {children}
    </div>
  );
}

export function OverlayHeader({
  back,
  onBack,
  title,
  subtitle,
  right,
  border = false,
  children,
  pb = 12,
}: {
  back: string;
  onBack: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  border?: boolean;
  children?: ReactNode;
  pb?: number;
}) {
  return (
    <div
      className={cx('shrink-0 px-5 pt-[calc(var(--sat)+4px)]', border && 'border-b border-line-soft')}
      style={{ paddingBottom: pb }}
    >
      <div className="flex items-center justify-between">
        <BackLink label={back} onClick={onBack} />
        {right}
      </div>
      {title && (
        <div className="mt-[10px] font-display text-[26px] font-bold tracking-[-0.02em]">{title}</div>
      )}
      {subtitle && <div className="mt-[3px] text-[13px] text-slate-500">{subtitle}</div>}
      {children}
    </div>
  );
}

export function OverlayFooter({
  children,
  border = true,
  className,
}: {
  children: ReactNode;
  border?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cx('shrink-0 bg-white px-5 pt-3', border && 'border-t border-line-soft', className)}
      style={{ paddingBottom: 'max(calc(var(--sab) + 6px), 20px)' }}
    >
      {children}
    </div>
  );
}

/**
 * Shows a fixed-size preview (an ID card is 340×214, the size it prints at) scaled down to fit narrow
 * phones instead of overflowing them.
 */
export function FitWidth({
  width,
  height,
  children,
}: {
  width: number;
  height: number;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setScale(Math.min(1, el.clientWidth / width));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);
  return (
    <div ref={ref} className="mx-auto w-full" style={{ maxWidth: width, height: height * scale }}>
      <div
        style={{
          width,
          height,
          transform: scale < 1 ? `scale(${scale})` : undefined,
          transformOrigin: '0 0',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** Bottom sheet with grabber + dimmed backdrop (design "sheets", z-40). */
export function BottomSheet({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div className="absolute inset-0 z-40 flex flex-col justify-end">
      {/* fixed, so on wide screens it dims the whole window, not just the app column */}
      <div className="fixed inset-0 animate-fade-in-fast bg-[rgba(15,23,42,0.42)]" onClick={onClose} />
      <div
        className="relative flex max-h-[88%] animate-sheet-up flex-col rounded-t-[28px] bg-white px-5 pt-3"
        style={{ paddingBottom: 'max(calc(var(--sab) + 6px), 24px)' }}
      >
        <div className="mx-auto mb-4 h-[5px] w-10 shrink-0 rounded-[3px] bg-slate-200" />
        {children}
      </div>
    </div>
  );
}

export function ListCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('flex flex-col overflow-hidden rounded-2xl border border-line', className)}>
      {children}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="px-[10px] py-[30px] text-center text-[13.5px] text-slate-400">{children}</div>;
}

// ---------------------------------------------------------------- feedback

export function ToastHost() {
  const t = useToast();
  if (!t) return null;
  return (
    <div
      key={t.id}
      className="absolute left-1/2 z-[60] flex max-w-[92%] -translate-x-1/2 animate-fade-up items-center gap-[9px] rounded-[13px] bg-ink px-[18px] py-[11px] text-[13px] font-medium text-white shadow-[0_10px_28px_rgba(0,0,0,0.25)]"
      style={{ top: 'calc(var(--sat) + 4px)' }}
      role="status"
    >
      <div className="h-[7px] w-[7px] shrink-0 rounded-full bg-sky-400" />
      <span className="truncate">{t.msg}</span>
    </div>
  );
}

// ---------------------------------------------------------------- QR

/** Real QR code drawn as a module grid (the design's 21×21 look). */
export function QrGrid({
  payload,
  size,
  padding = 0,
  color = '#0f172a',
  className,
}: {
  payload: string;
  size: number;
  padding?: number;
  color?: string;
  className?: string;
}) {
  const { size: n, cells } = useMemo(() => qrMatrix(payload), [payload]);
  return (
    <svg
      width={size}
      height={size}
      viewBox={`${-padding} ${-padding} ${n + padding * 2} ${n + padding * 2}`}
      shapeRendering="crispEdges"
      className={cx('shrink-0 bg-white', className)}
      role="img"
      aria-label="QR code"
    >
      {cells.map((on, i) =>
        on ? <rect key={i} x={i % n} y={Math.floor(i / n)} width={1} height={1} fill={color} /> : null,
      )}
    </svg>
  );
}

export { cx };
